import styled, { keyframes } from "styled-components";
import { useState, useRef, useEffect } from "react";
import { FaPaste, FaEnvelope, FaWhatsapp } from "react-icons/fa";
import { toast } from "react-toastify";

interface VerificacaoModalProps {
  show: boolean;
  onHide: () => void;
  onCodeSubmit: (code: string) => Promise<void> | void;
  timer: number;
  onResend: () => void;
}

// ================= ANIMACOES =================
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
`;

// ================= ESTILOS =================
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: ${fadeIn} 0.15s ease-out;
  padding: 20px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 440px;
  animation: ${scaleIn} 0.25s ease-out;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 32px 32px 0;
  text-align: center;
`;

const IconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #3b82f6;
  font-size: 1.3rem;
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
`;

const Description = styled.p`
  font-size: 0.88rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

const ChannelBadges = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
`;

const ChannelBadge = styled.span<{ variant: 'email' | 'whatsapp' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${p => p.variant === 'whatsapp' ? '#ecfdf5' : '#eff6ff'};
  color: ${p => p.variant === 'whatsapp' ? '#166534' : '#1e40af'};
`;

const Body = styled.div`
  padding: 28px 32px;
`;

const CodeLabel = styled.p`
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px;
  text-align: center;
`;

const CodeRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;

  @media (max-width: 400px) {
    gap: 5px;
  }
`;

const CodeInput = styled.input<{ hasValue: boolean }>`
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  border: 2px solid ${p => p.hasValue ? '#3b82f6' : '#e2e8f0'};
  border-radius: 12px;
  background: ${p => p.hasValue ? '#eff6ff' : '#f8fafc'};
  transition: all 0.2s ease;
  caret-color: #3b82f6;
  font-family: 'Inter', 'Segoe UI', monospace;

  &:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    background: #ffffff;
  }

  @media (max-width: 400px) {
    width: 42px;
    height: 50px;
    font-size: 1.3rem;
  }
`;

const PasteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 auto;
  padding: 8px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #334155;
  }
`;

const TimerContainer = styled.div`
  text-align: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

const TimerText = styled.span<{ urgent: boolean }>`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${p => p.urgent ? '#ef4444' : '#64748b'};
  font-variant-numeric: tabular-nums;
`;

const TimerLabel = styled.span`
  font-size: 0.82rem;
  color: #94a3b8;
  margin-right: 6px;
`;

const ResendBtn = styled.button<{ enabled: boolean }>`
  display: block;
  margin: 10px auto 0;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${p => p.enabled ? '#3b82f6' : '#94a3b8'};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: ${p => p.enabled ? 'pointer' : 'default'};
  transition: color 0.2s;

  &:hover {
    color: ${p => p.enabled ? '#1d4ed8' : '#94a3b8'};
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 32px 28px;
`;

const BtnSecondary = styled.button`
  flex: 1;
  padding: 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  color: #475569;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const BtnPrimary = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: ${p => p.disabled ? '#cbd5e1' : '#3b82f6'};
  color: #ffffff;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover {
    background: ${p => p.disabled ? '#cbd5e1' : '#2563eb'};
  }
`;

// ================= COMPONENTE =================
const VerificacaoModal: React.FC<VerificacaoModalProps> = ({ show, onHide, onCodeSubmit, timer, onResend }) => {
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [canResend, setCanResend] = useState(false);
  const [remainingTime, setRemainingTime] = useState(timer);
  const [submitting, setSubmitting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  useEffect(() => {
    if (show) {
      setRemainingTime(timer);
      setCode(Array(6).fill(''));
      setSubmitting(false);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (show) {
      interval = setInterval(() => {
        setRemainingTime(prev => {
          const next = Math.max(0, prev - 1);
          if (next === 0) setCanResend(true);
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [show]);

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Caso o usuário cole diretamente no input
      const chars = value.toUpperCase().split('').slice(0, 6 - index);
      const newCode = [...code];
      chars.forEach((char, i) => { if (index + i < 6) newCode[index + i] = char; });
      setCode(newCode);
      const nextIndex = Math.min(index + chars.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    const newCode = [...code];
    newCode[index] = value.toUpperCase();
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const newCode = [...code];
      newCode[index - 1] = '';
      setCode(newCode);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim().slice(0, 6).toUpperCase().split('');
    const newCode = Array(6).fill('');
    pasteData.forEach((char, i) => newCode[i] = char);
    setCode(newCode);
    inputRefs.current[Math.min(pasteData.length, 5)]?.focus();
  };

  const handlePasteClick = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const clean = text.trim().slice(0, 6).toUpperCase();
      if (clean) {
        const newCode = Array(6).fill('');
        clean.split('').forEach((char, i) => newCode[i] = char);
        setCode(newCode);
        toast.success("Código colado!");
      }
    } catch {
      toast.error("Não foi possível acessar a área de transferência.");
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const fullCode = code.join('');
    if (fullCode.length === 6) {
      setSubmitting(true);
      try {
        await onCodeSubmit(fullCode);
      } catch {
        setSubmitting(false);
      }
    }
  };

  const handleResend = () => {
    if (canResend) {
      setCanResend(false);
      setRemainingTime(timer);
      onResend();
    }
  };

  const isCodeComplete = code.every(c => c !== '');
  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;
  const isUrgent = remainingTime < 60;

  if (!show) return null;

  return (
    <Overlay onClick={onHide}>
      <Card onClick={e => e.stopPropagation()}>
        <Header>
          <IconCircle><FaEnvelope /></IconCircle>
          <Title>Verifique seu e-mail ou WhatsApp</Title>
          <Description>
            Enviamos um código de 6 dígitos para confirmar sua identidade
          </Description>
          <ChannelBadges>
            <ChannelBadge variant="email"><FaEnvelope size={10} /> E-mail</ChannelBadge>
            <ChannelBadge variant="whatsapp"><FaWhatsapp size={10} /> WhatsApp</ChannelBadge>
          </ChannelBadges>
        </Header>

        <Body>
          <CodeLabel>Digite o código recebido</CodeLabel>
          <CodeRow>
            {code.map((digit, index) => (
              <CodeInput
                key={index}
                type="text"
                maxLength={1}
                value={digit}
                hasValue={digit !== ''}
                onChange={e => handleInputChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={handlePaste}
                ref={el => (inputRefs.current[index] = el)}
                autoComplete="off"
                inputMode="text"
              />
            ))}
          </CodeRow>

          <PasteBtn type="button" onClick={handlePasteClick}>
            <FaPaste size={12} /> Colar código
          </PasteBtn>

          <TimerContainer>
            <TimerLabel>Expira em</TimerLabel>
            <TimerText urgent={isUrgent}>
              {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
            </TimerText>
            <ResendBtn
              type="button"
              enabled={canResend}
              onClick={handleResend}
            >
              {canResend ? 'Reenviar código' : 'Não recebeu? Verifique o spam'}
            </ResendBtn>
          </TimerContainer>
        </Body>

        <Footer>
          <BtnSecondary type="button" onClick={onHide}>Cancelar</BtnSecondary>
          <BtnPrimary type="button" disabled={!isCodeComplete || submitting} onClick={handleSubmit}>
            {submitting ? 'Verificando...' : 'Verificar'}
          </BtnPrimary>
        </Footer>
      </Card>
    </Overlay>
  );
};

export default VerificacaoModal;
